import React from 'react';
import { Building, CheckCircle2, Target, ArrowRight } from 'lucide-react';

export default function BasketInterface({ basket }) {
    if (!basket) return null;

    const colors = ['#10B981', '#3B82F6', '#8B5CF6', '#EC4899'];
    const colorClasses = ['bg-emerald-500', 'bg-blue-500', 'bg-purple-500', 'bg-pink-500'];

    return (
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 md:p-8 border border-purple-100 shadow-lg">
            <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-purple-600 rounded-xl">
                    <Building className="text-white" size={24} />
                </div>
                <div>
                    <h3 className="text-lg font-black text-gray-900 uppercase tracking-tight">Diversified Carbon Basket</h3>
                    <p className="text-xs text-gray-600 font-medium">Instant portfolio diversification</p>
                </div>
            </div>

            {/* Basket Header */}
            <div className="bg-white rounded-xl p-6 border border-purple-100 mb-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    <div>
                        <p className="text-[9px] font-black text-gray-500 uppercase tracking-widest mb-1">Basket ID</p>
                        <p className="text-sm font-bold text-gray-900">{basket.basket_id}</p>
                    </div>
                    <div>
                        <p className="text-[9px] font-black text-gray-500 uppercase tracking-widest mb-1">Diversification</p>
                        <p className="text-sm font-black text-purple-600">{basket.diversification_score}</p>
                    </div>
                    <div>
                        <p className="text-[9px] font-black text-gray-500 uppercase tracking-widest mb-1">Risk Rating</p>
                        <p className="text-sm font-black text-gray-900">{basket.risk_rating}</p>
                    </div>
                    <div>
                        <p className="text-[9px] font-black text-gray-500 uppercase tracking-widest mb-1">Expected ROI</p>
                        <p className="text-sm font-black text-emerald-600">{basket.expected_roi}</p>
                    </div>
                </div>
            </div>

            {/* Composition Chart */}
            <div className="mb-8">
                <h4 className="text-sm font-bold text-gray-700 mb-4 uppercase tracking-wide">Basket Composition</h4>

                <div className="flex flex-col md:flex-row gap-8 items-center">
                    {/* Pie Chart */}
                    <div className="relative w-48 h-48">
                        <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
                            {basket.composition.reduce((acc, item, index) => {
                                const offset = acc.offset;
                                const dasharray = `${item.allocation_percentage} ${100 - item.allocation_percentage}`;

                                acc.elements.push(
                                    <circle
                                        key={index}
                                        r="40"
                                        cx="50"
                                        cy="50"
                                        fill="transparent"
                                        stroke={colors[index % colors.length]}
                                        strokeWidth="20"
                                        strokeDasharray={dasharray}
                                        strokeDashoffset={-offset}
                                        className="transition-all duration-500"
                                    />
                                );
                                acc.offset += item.allocation_percentage;
                                return acc;
                            }, { offset: 0, elements: [] }).elements}
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="text-center">
                                <p className="text-2xl font-black text-gray-900">{basket.composition.length}</p>
                                <p className="text-[9px] font-bold text-gray-500 uppercase">Projects</p>
                            </div>
                        </div>
                    </div>

                    {/* Legend */}
                    <div className="flex-1 grid grid-cols-1 gap-3">
                        {basket.composition.map((item, index) => (
                            <div key={index} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-100">
                                <div className={`w-4 h-4 rounded-full ${colorClasses[index % colorClasses.length]}`}></div>
                                <div className="flex-1">
                                    <p className="text-xs font-bold text-gray-900">{item.project_name}</p>
                                    <p className="text-[9px] text-gray-500">{item.credit_type} • Vintage {item.vintage}</p>
                                </div>
                                <p className="text-sm font-black text-gray-900">{item.allocation_percentage}%</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Allocation Bar */}
            <div className="mb-8">
                <h4 className="text-sm font-bold text-gray-700 mb-4 uppercase tracking-wide">Allocation Breakdown</h4>
                <div className="flex h-6 rounded-full overflow-hidden">
                    {basket.composition.map((item, index) => (
                        <div
                            key={index}
                            className={`${colorClasses[index % colorClasses.length]} transition-all hover:opacity-80`}
                            style={{ width: `${item.allocation_percentage}%` }}
                            title={`${item.project_name}: ${item.allocation_percentage}%`}
                        />
                    ))}
                </div>
            </div>

            {/* Benefits */}
            <div className="mb-8">
                <h4 className="text-sm font-bold text-gray-700 mb-4 uppercase tracking-wide">Basket Benefits</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {basket.benefits.map((benefit, index) => (
                        <div key={index} className="flex items-center gap-3 p-4 bg-white rounded-xl border border-purple-100">
                            <CheckCircle2 className="text-purple-600 shrink-0" size={20} />
                            <p className="text-sm font-bold text-gray-700">{benefit}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* SDG Alignment */}
            <div className="mb-8">
                <h4 className="text-sm font-bold text-gray-700 mb-4 uppercase tracking-wide">SDG Alignment</h4>
                <div className="flex gap-4 flex-wrap">
                    {basket.sdg_alignment.map((sdg, index) => (
                        <div key={index} className="flex flex-col items-center gap-2">
                            <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center p-2 border border-gray-100 shadow-sm">
                                <img src={sdg.icon} alt={sdg.name} className="w-full h-full object-contain" />
                            </div>
                            <p className="text-[9px] font-black text-gray-600 uppercase tracking-wider text-center">{sdg.name}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Purchase Info */}
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl p-6 text-white">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6">
                    <div>
                        <p className="text-[9px] font-black text-purple-200 uppercase tracking-widest mb-1">Token Price</p>
                        <p className="text-2xl font-black">AED {basket.token_price.toLocaleString()}</p>
                    </div>
                    <div>
                        <p className="text-[9px] font-black text-purple-200 uppercase tracking-widest mb-1">Total Capacity</p>
                        <p className="text-2xl font-black">{(basket.total_capacity / 1000).toFixed(0)}K</p>
                        <p className="text-[9px] font-bold text-purple-200">tCO₂e</p>
                    </div>
                    <div>
                        <p className="text-[9px] font-black text-purple-200 uppercase tracking-widest mb-1">Available</p>
                        <p className="text-2xl font-black">{(basket.tokens_available / 1000).toFixed(0)}K</p>
                        <p className="text-[9px] font-bold text-purple-200">tokens</p>
                    </div>
                    <div>
                        <p className="text-[9px] font-black text-purple-200 uppercase tracking-widest mb-1">Sold</p>
                        <p className="text-2xl font-black">{Math.round(((basket.total_capacity - basket.tokens_available) / basket.total_capacity) * 100)}%</p>
                    </div>
                </div>

                {/* Progress Bar */}
                <div className="relative h-3 w-full bg-white/20 rounded-full overflow-hidden mb-6">
                    <div
                        className="absolute inset-y-0 left-0 bg-white rounded-full shadow-[0_0_10px_rgba(255,255,255,0.3)]"
                        style={{ width: `${((basket.total_capacity - basket.tokens_available) / basket.total_capacity) * 100}%` }}
                    />
                </div>

                <button className="w-full px-6 py-4 bg-white hover:bg-purple-50 text-purple-600 rounded-xl text-sm font-black uppercase tracking-wider transition-all flex items-center justify-center gap-2">
                    <Target size={18} />
                    Purchase Basket Units
                    <ArrowRight size={18} />
                </button>
            </div>
        </div>
    );
}
